import * as R from 'ramda';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { IJob } from '../../store/aiearth/types';
import { CeresLogger } from '../../logger';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col,
  Spinner,
  Button,
  Table,
  Tab,
  Tabs,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import styled from 'styled-components';
import { ChevronLeft } from 'react-feather';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import TreeView from './TreeViewData';
import FormControl from '@material-ui/core/FormControl';
import Paper from '@material-ui/core/Paper';
import DataTable from 'react-data-table-component';
import { searchAIEarth } from '../../services/firebase';
import { IElasticResult, ISearchRecord } from '../../types/search';
import { Scrollbars } from 'react-custom-scrollbars';
import { IAuthContext } from '../../context/auth/auth-context';
import { TIERS } from '../../types/tiers';
import { toast } from 'react-toastify';
import { updateJob } from '../../store/aiearth/action';
import SoilSummary from './findings/SoilSummary';
import DatabaseDescription from './findings/DatabaseDescription';
import { API_BASE_URL, API_AUTH_HEADERS } from '../../util/api.util';
import { glassPanelBase } from '../ui/mysites';
import { commonColors } from '../ui/commonStyles';
import {
  FINDINGS_OPTIONS,
  getFindingsPanelMinWidth,
  getFindingsTypeLabel,
  isAdjoiningFindingsType,
} from './findingsPanelLayout';

const selectedRowBg = 'rgba(52, 211, 153, 0.28) !important';
const selectedRowColor = '#f8fafc !important';

interface IMapsFinding {
  gMap: any;
  gMaps: any;
  doStorageDownload: any;
  getStorageRef: any;
  setDownloadTriggers: any;
  downloadTriggers: any;
  context: IAuthContext;
  selectedEntityNumber: any;
  mapFindingAutoScroll?: boolean;
  onMapFindingAutoScrollConsumed?: () => void;
}
const PanelShell = styled(Col)`
  border-radius: 1.25rem;
  overflow: hidden;
  margin-left: 0.75rem;
  ${glassPanelBase};
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const FindingsPanelShell = styled(PanelShell)<{ $minWidth?: number }>`
  height: 55vh;
  max-height: 55vh;
  ${({ $minWidth }) =>
    $minWidth
      ? `
    min-width: ${$minWidth}px;
    width: fit-content;
  `
      : ''}
`;

const EntityPanelShell = styled(PanelShell)`
  height: 55vh;
  max-height: 55vh;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: ${commonColors.tableHeaderBg};
  border-bottom: 1px solid ${commonColors.borderSoft};
`;

const PanelBackButton = styled(Button)`
  && {
    padding: 5px;
    background: rgba(52, 211, 153, 0.15);
    border: 1px solid rgba(52, 211, 153, 0.45);
    color: ${commonColors.accent};

    &:hover,
    &:focus {
      background: rgba(52, 211, 153, 0.25);
      border-color: ${commonColors.accent};
      color: ${commonColors.accentHover};
      box-shadow: none;
    }
  }
`;

const FilterFindings = styled.div`
  flex: 1;
  min-width: 12rem;
`;

const FindingsDropdownPaper = styled(Paper)`
  background: rgba(10, 10, 12, 0.96) !important;
  color: ${commonColors.textPrimary} !important;
  border: 1px solid ${commonColors.borderMid} !important;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45) !important;

  .MuiAutocomplete-option,
  .MuiAutocomplete-listbox li {
    color: ${commonColors.textPrimary} !important;
    background: transparent !important;
  }

  .MuiAutocomplete-option[data-focus='true'],
  .MuiAutocomplete-listbox li[data-focus='true'] {
    background: rgba(52, 211, 153, 0.18) !important;
  }

  .MuiAutocomplete-option[aria-selected='true'],
  .MuiAutocomplete-listbox li[aria-selected='true'] {
    background: rgba(52, 211, 153, 0.24) !important;
  }
`;

const CustomFormControl = styled(FormControl)`
  width: 100%;
  border-radius: 4px;

  .MuiInputLabel-root {
    color: ${commonColors.textMuted};
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${commonColors.accent};
  }

  .MuiOutlinedInput-root {
    color: ${commonColors.textPrimary};
    background: rgba(2, 6, 23, 0.35);
    border-radius: 8px;

    input::selection {
      background: rgba(52, 211, 153, 0.35);
      color: ${commonColors.textPrimary};
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: ${commonColors.borderMid};
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: rgba(148, 163, 184, 0.5);
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${commonColors.accent};
    }
  }

  .MuiAutocomplete-popupIndicator,
  .MuiAutocomplete-clearIndicator {
    color: ${commonColors.textMuted};
  }

  .MuiAutocomplete-inputRoot {
    flex-wrap: nowrap;
  }

  .MuiAutocomplete-input {
    min-width: 0;
    text-overflow: clip;
    overflow: visible;
    white-space: nowrap;
  }
`;

const JobTasksContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  overflow: auto;
  color: ${commonColors.textPrimary};
  background: transparent;
  scrollbar-color: rgba(148, 163, 184, 0.45) rgba(15, 23, 42, 0.6);
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.6);
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(148, 163, 184, 0.45);
    border-radius: 20px;
  }

  &::-webkit-scrollbar-corner {
    background: rgba(15, 23, 42, 0.6);
  }
`;

const JobDetailsContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 10px 12px;
  overflow: auto;
  color: ${commonColors.textPrimary};
  background: transparent;

  h6,
  h6 a {
    color: ${commonColors.accent};
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 68, 68, 0.25);
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(148, 163, 184, 0.45);
    border-radius: 20px;
  }
`;

const StyledDataTable = styled(DataTable)`
  background-color: transparent !important;
  display: block;
  width: max-content;
  min-width: 100%;

  & > div {
    background-color: transparent !important;
  }

  & .rdt_Table {
    background-color: transparent !important;
    color: ${commonColors.textPrimary};
    width: max-content;
    min-width: 100%;
  }

  & .rdt_TableHead {
    background: ${commonColors.tableHeaderBg};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  & .rdt_TableHeadRow {
    min-height: 36px;
    background: ${commonColors.tableHeaderBg};
    border-bottom: 1px solid ${commonColors.borderSoft};
  }

  & .rdt_TableCol {
    padding: 8px 10px;
    text-transform: uppercase;
    color: ${commonColors.textMuted};
    font-size: 10px;
  }

  & .rdt_TableCell {
    font-size: 10px;
    padding: 3px 8px;
    width: 9rem;
    text-overflow: ellipsis;
    color: ${commonColors.textPrimary};
    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  }

  & .rdt_TableBody {
    overflow: visible !important;
    background-color: transparent !important;
  }

  & .rdt_TableRow {
    min-height: 36px;
    background: transparent;
    color: ${commonColors.textPrimary};

    &:hover {
      background: rgba(30, 41, 59, 0.55);
      cursor: pointer;
    }
  }

  & .rdt_Pagination {
    display: none;
  }
`;
const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2em 0em;
`;
const TableAstm = styled(Table)`
  width: 704px;
  color: ${commonColors.textPrimary};

  thead th {
    background: ${commonColors.tableHeaderBg};
    color: ${commonColors.textMuted};
    border-color: ${commonColors.borderSoft};
  }

  tbody td {
    color: ${commonColors.textPrimary};
    border-color: rgba(148, 163, 184, 0.15);
  }
`;
const TD = styled.td`
  font-size: 12px;
  color: ${commonColors.textPrimary};
`;
const TH = styled.th`
  font-size: 12px;
  text-transform: uppercase;
  color: ${commonColors.textMuted};
`;
const CustomTabs = styled(Tabs)`
  border-bottom: 1px solid ${commonColors.borderSoft} !important;

  .nav-link {
    color: ${commonColors.textMuted};
    font-size: 0.85rem;
  }

  .nav-link.active {
    color: ${commonColors.accent};
    background: transparent;
    border-color: ${commonColors.accent} ${commonColors.accent} transparent;
  }
`;
const OLContainer = styled.ol`
  padding-inline-start: 10px !important;
  font-size: 0.6rem;
  padding-top: 7px;
  margin-bottom: 0px;
  color: ${commonColors.textPrimary};
`;
const DownloadHyperlink = styled.a`
  color: ${commonColors.accent};
  cursor: pointer;
  &:hover {
    color: ${commonColors.accentHover} !important;
    text-decoration: underline !important;
  }
`;
const AddressText = styled.span`
  font-size: 0.6rem;
  color: ${commonColors.textMuted};
`;
const EnameText = styled.h6`
  color: ${commonColors.textPrimary} !important;
  font-weight: 700;
`;
const SpaceEvenly = styled.div`
  display: flex;
  justify-content: space-between;
`;
const EPALink = styled.span`
  color: ${commonColors.accent};
  cursor: pointer;
`;

const fetchRecordsByRN = (
  rnNumber: string,
  searchTerm: string,
  searchType: string
) => {
  return searchAIEarth({
    query: {
      from: 0,
      size: 50,
      sortByReleaseDate: true,
      text: searchTerm,
      type: searchType,
      terms: {
        regulated_entity_number: rnNumber,
      },
    },
  });
};

const RecordTable: React.FC<{
  columns: any;
  data: any;
  onFindingClick?: any;
  findingsRowStyles?: any;
}> = (props) => {
  return (
    <StyledDataTable
      columns={props.columns}
      data={props.data}
      noHeader={true}
      fixedHeader={false}
      pointerOnHover
      sortServer={true}
      pagination={false}
      onRowClicked={props.onFindingClick || undefined}
      highlightOnHover
      conditionalRowStyles={props.findingsRowStyles}
    />
  );
};

const hasJobFeatureData = (feature: any) =>
  Boolean(
    feature
    && (
      (typeof feature.entity_number !== 'undefined' && feature.entity_number)
      || (typeof feature.ogc_fid !== 'undefined' && feature.ogc_fid)
    )
  );

const openJobFeature = (
  jobState: IJob,
  dispatch: ReturnType<typeof useDispatch>,
  row: any,
  options?: { mapFindingTab?: boolean; autoScroll?: boolean }
) => {
  dispatch(
    updateJob({
      ...jobState,
      jobInit: false,
      jobFeatureTab: true,
      mapFindingTab: options?.mapFindingTab ?? jobState.mapFindingTab,
      jobFeature: row,
    })
  );
};

const MyTundraMapsFinding: React.FC<IMapsFinding> = (props) => {
  const {
    context: authContext,
    mapFindingAutoScroll,
    onMapFindingAutoScrollConsumed,
  } = props;
  const dispatch = useDispatch();
  const store: any = useSelector<any>((state): any => state);
  const jobState: IJob = store.job;
  const [getAutoScroll, setAutoScroll] = useState<boolean>(false);
  const [entityNumber, setEntityNumber] = useState<any>();
  const [getEntity, setEntity] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [entityDetails, setEntityDetails] = useState();
  const [orphansFindings, setOrphansFindings] = useState<any>([]);
  const [relatedRecords, setRelatedRecords] = useState<ISearchRecord[]>([]);
  const documentsTabTitle = `Documents(${relatedRecords.length})`;
  const findingsPanelMinWidth = useMemo(
    () => getFindingsPanelMinWidth(jobState.mapFindingsTypes),
    [jobState.mapFindingsTypes]
  );
  const findingsDropdownValue = getFindingsTypeLabel(jobState.mapFindingsTypes);

  const getZoomPoint = useCallback((location: string) => {
    if (!location) {
      return;
    }
    const normalized = location.replace('POINT(', '').replace(')', '');
    const latLng = normalized.split(' ');
    const lat = latLng[1];
    const lng = latLng[0];
    if (lat && lng && props.gMap && props.gMaps) {
      const latlng = new props.gMaps.LatLng(lat, lng);
      props.gMap.panTo(latlng);
      props.gMap.setZoom(18);
    }
  }, [props.gMap, props.gMaps]);

  const selectJobFeature = useCallback(
    (row: any, options?: { mapFindingTab?: boolean; autoScroll?: boolean }) => {
      if (!hasJobFeatureData(row)) {
        return;
      }
      if (options?.autoScroll !== false) {
        setAutoScroll(false);
      }
      if (row.entity_number) {
        setEntityNumber(row.entity_number);
      }
      setEntity(row);
      setIsLoading(Boolean(row.entity_number));
      if (row.geom) {
        getZoomPoint(row.geom);
      }
      openJobFeature(jobState, dispatch, row, options);
    },
    [dispatch, getZoomPoint, jobState]
  );

  const onFindingClick = (row: any) => {
    selectJobFeature(row, { mapFindingTab: true, autoScroll: false });
  };
  const onOrphansClick = (row: any) => {
    setOrphansFindings(row);
    selectJobFeature(row, { mapFindingTab: true, autoScroll: false });
  };

  const propertiesCol = [
    {
      name: 'ID',
      selector: 'map_id',
      width: '40px',
    },
    {
      name: 'Vec',
      selector: 'vec_status',
      width: '80px',
    },
    {
      name: 'Rec',
      selector: 'rec_status',
      width: '80px',
    },
    {
      name: 'Entity Number',
      selector: 'entity_number',
      width: '120px',
    },
    {
      name: 'Entity Name',
      selector: 'entity_name',
      width: '120px',
    },
    {
      name: 'Address',
      selector: 'address',
      width: '150px',
    },
    {
      name: 'City',
      selector: 'city',
      width: '80px',
    },
    {
      name: 'State',
      selector: 'state',
      width: '50px',
    },
    {
      name: 'Zip',
      selector: 'zip_code',
      width: '60px',
    },
    {
      name: 'Programs',
      selector: 'programs',
      width: '70px',
    },
    {
      name: 'GW',
      selector: 'gw_flow',
      width: '70px',
    },
    {
      name: 'Elevation',
      selector: 'elevation',
      width: '70px',
    },
    {
      name: 'Distance',
      selector: 'distance_feet',
      width: '70px',
    },
  ];
  const orphansCol = [
    {
      name: 'Entity Name',
      selector: 'entity_name',
    },
    {
      name: 'Entity Number',
      selector: 'entity_number',
    },
    {
      name: 'Entity Source',
      selector: 'entity_source',
    },
    {
      name: 'Database',
      selector: 'programs',
    },
    {
      name: 'Address',
      selector: 'address',
    },
    {
      name: 'City',
      selector: 'city',
    },
    {
      name: 'Zip',
      selector: 'zip_code',
    },
    {
      name: 'Country',
      selector: 'county',
    },
    {
      name: 'Symbol',
      selector: 'program_symbol',
    },
    {
      name: 'State',
      selector: 'state',
    },
    {
      name: 'Secondary ID',
      selector: 'secondary_id',
    },
    {
      name: 'Street Name',
      selector: 'street_name',
    },
  ];
  const adjoiningCol = [
    {
      name: 'Address',
      selector: 'address',
    },
    {
      name: 'City',
      selector: 'city',
    },
    {
      name: 'County',
      selector: 'county',
    },
    {
      name: 'Direction',
      selector: 'direction',
    },
    {
      name: 'Distance',
      selector: 'distance_feet',
    },
    {
      name: 'Elevation',
      selector: 'elevation_feet',
    },
    {
      name: 'GW',
      selector: 'gw_flow',
    },
    {
      name: 'Legal',
      selector: 'legal',
    },
    {
      name: 'Mail Address',
      selector: 'mail_addr',
    },
    {
      name: 'Name',
      selector: 'name',
    },
    {
      name: 'Owner',
      selector: 'owner',
    },
    {
      name: 'State',
      selector: 'state',
    },
  ];

  useEffect(() => {
    async function getMapsFindingSummary() {
      dispatch(
        updateJob({
          ...jobState,
          ...{
            searchFinding: false,
          },
        })
      );
      try {
        axios
          .get(
            `${process.env.REACT_APP_CERES_URL}/getMapsFindingSummaryPhase1?v_rn_number=${props.selectedEntityNumber}`
            // 'http://localhost:5000/ceres-platform-test/us-central1/api/document-entity/list',
          )
          .then((response) => {
            const detailsJson = response.data[0].esadata_findings;
            dispatch(
              updateJob({
                ...jobState,
                ...{
                  esadataFindings: detailsJson,
                  searchFinding: false,
                },
              })
            );
          })
          .catch((err) => {
            CeresLogger.error(err);
          });
      } catch (e) {
        CeresLogger.error(e);
      }
    }
    getMapsFindingSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobState.mapFindingsTypes,
  ]);

  useEffect(() => {
    if (!jobState.jobFeatureTab) {
      setEntityNumber(undefined);
      setEntity(undefined);
      setEntityDetails(undefined);
      setRelatedRecords([]);
      setIsLoading(false);
    }
  }, [jobState.jobFeatureTab]);

  useEffect(() => {
    if (!mapFindingAutoScroll) {
      return;
    }
    setAutoScroll(true);
    onMapFindingAutoScrollConsumed?.();
  }, [mapFindingAutoScroll, onMapFindingAutoScrollConsumed]);

  useEffect(() => {
    if (!jobState.jobFeatureTab) {
      return;
    }
    const feature = jobState.jobFeature;
    if (!hasJobFeatureData(feature)) {
      return;
    }
    const rn = feature.entity_number;
    if (!rn) {
      if (!getEntity) {
        setEntity(feature);
      }
      return;
    }
    if (getEntity?.entity_number === rn && entityNumber === rn) {
      return;
    }
    setEntityNumber(rn);
    setEntity(feature);
    setIsLoading(true);
    if (feature.geom) {
      getZoomPoint(feature.geom);
    }
  }, [jobState.jobFeatureTab, jobState.jobFeature, entityNumber, getEntity, getZoomPoint]);

  useEffect(() => {
    if (!entityNumber) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadEntityPanel = async () => {
      try {
        const [detailsResult, recordsResult] = await Promise.allSettled([
          axios.post(
            API_BASE_URL + '/api/programQueryDetails',
            { RN: entityNumber },
            { headers: API_AUTH_HEADERS }
          ),
          fetchRecordsByRN(entityNumber, '', 'must'),
        ]);

        if (cancelled) {
          return;
        }

        if (detailsResult.status === 'fulfilled') {
          const detailsJson = detailsResult.value.data[0]?.get_prgm_json_for_entity;
          setEntityDetails(detailsJson);
        } else {
          CeresLogger.error(detailsResult.reason);
        }

        if (recordsResult.status === 'fulfilled') {
          const data = recordsResult.value.data as IElasticResult<ISearchRecord>;
          const records = (data && data.records) || [];
          records.forEach((rec) => {
            rec.containsSearchTerm = true;
          });
          setRelatedRecords(records);
        } else {
          CeresLogger.error(recordsResult.reason);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadEntityPanel();

    return () => {
      cancelled = true;
    };
  }, [entityNumber]);

  useEffect(() => {
    if (!getAutoScroll || !jobState.jobFeature) {
      return;
    }
    const entityNumberMatch = jobState.jobFeature.entity_number;
    const ogcFidMatch = jobState.jobFeature.ogc_fid;
    let rowId: string | null = null;

    if (entityNumberMatch && Array.isArray(jobState.mapFindings)) {
      const index = jobState.mapFindings.findIndex(
        (row: any) => row.entity_number === entityNumberMatch
      );
      if (index >= 0) {
        rowId = `row-${index}`;
      }
    } else if (ogcFidMatch && Array.isArray(jobState.mapFindings)) {
      const index = jobState.mapFindings.findIndex(
        (row: any) => row.ogc_fid === ogcFidMatch
      );
      if (index >= 0) {
        rowId = `row-${index}`;
      }
    }

    if (rowId) {
      document.getElementById(rowId)?.scrollIntoView();
    }
  }, [getAutoScroll, jobState.jobFeature, jobState.mapFindings]);

  const addjoinFindingsRowStyles = [
    {
      when: (row: any) =>
        Boolean(jobState.jobFeature?.ogc_fid)
        && row.ogc_fid === jobState.jobFeature.ogc_fid,
      style: {
        // tslint:disable-next-line: object-literal-key-quotes
        backgroundColor: selectedRowBg,
        // tslint:disable-next-line: object-literal-key-quotes
        color: selectedRowColor,
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  ];

  const findingsRowStyles = [
    {
      when: (row: any) =>
        Boolean(jobState.jobFeature?.entity_number)
        && row.entity_number === jobState.jobFeature.entity_number,
      style: {
        // tslint:disable-next-line: object-literal-key-quotes
        backgroundColor: selectedRowBg,
        // tslint:disable-next-line: object-literal-key-quotes
        color: selectedRowColor,
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  ];
  const orphansRowStyles = [
    {
      when: (row: any) =>
        Boolean(orphansFindings?.entity_number)
        && row.entity_number === orphansFindings.entity_number,
      style: {
        // tslint:disable-next-line: object-literal-key-quotes
        backgroundColor: selectedRowBg,
        // tslint:disable-next-line: object-literal-key-quotes
        color: selectedRowColor,
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  ];

  const getRecordTitle = (item: ISearchRecord) =>
    `${item.title} - ${item.seriesName} - ${item.releaseDate}`;

  const getTCEQRecordUrl = (eNumber: string) =>
    `https://www15.tceq.texas.gov/crpub/index.cfm?fuseaction=regent.validateRE&re_ref_num_txt=${eNumber}`;

  const getEPARecordUrl = (eNumber: string) =>
    `https://ofmpub.epa.gov/frs_public2/fii_map_master.fii_retrieve?fac_search=facility_uin&fac_value=${eNumber}&fac_search_type=Equal+To&postal_code=&location_address=&add_search_type=B&city_name=&county_name=&state_code=&epa_region_code=&cong_dist=&legis_dist=&huc_code=&fed_agency=&TribalLand=0&selectTribe=noselect&sic_type=Equal+to&sic_code_to=&naic_type=Equal+to&naic_to=&org_name=&duns_num=&contact_name=&prog_search=&int_search=&search_type=&search_type=all&all_programs=YES&sysname=&page_no=1&output_sql_switch=TRUE&report=1&database_type=FII&tribal_ind=&last_facility=&univ_search=&fac_search_term=&tribetype=&triballand=&selecttribe=&tribedistance1=`;

  const displayEntity = getEntity || jobState.jobFeature;

  return (
    <>
      {jobState.mapFindingTab && !jobState.jobInit && (
        <FindingsPanelShell lg={3} $minWidth={findingsPanelMinWidth}>
          <PanelHeader>
            <PanelBackButton
              size="sm"
              onClick={() => {
                dispatch(
                  updateJob({
                    ...jobState,
                    ...{
                      jobInit: true,
                      jobFeatureTab: false,
                      mapFindingTab: false,
                    },
                  })
                );
              }}
            >
              <ChevronLeft />
            </PanelBackButton>
            <FilterFindings>
              <CustomFormControl size="small" variant="outlined">
                <Autocomplete
                  PaperComponent={FindingsDropdownPaper as any}
                  options={FINDINGS_OPTIONS}
                  value={findingsDropdownValue}
                  getOptionLabel={(option: any) => option}
                  getOptionSelected={(option: string, value: string) =>
                    option === value
                  }
                  onChange={(event: any, newValue: any) => {
                    dispatch(
                      updateJob({
                        ...jobState,
                        ...{
                          jobFeatureTab: false,
                          mapFindingsTypes: newValue,
                        },
                      })
                    );
                  }}
                  style={{ width: '100%' }}
                  disableClearable
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Findings"
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </CustomFormControl>
            </FilterFindings>
          </PanelHeader>
          <JobTasksContainer>
            {jobState.mapFindingsTypes === 'Job Map Entities' &&
              jobState.esaData &&
              Object.keys(jobState.esaData).length > 0 &&
              typeof jobState.esaData.regulated_entities_point.features !==
                'undefined' &&
              typeof jobState.esaData.regulated_entities_point.features[0]
                .geometry !== 'undefined' &&
              Object.keys(jobState.esaData.regulated_entities_point.features)
                .length > 0 &&
              jobState.esaData.regulated_entities_point && (
                <RecordTable
                  columns={propertiesCol}
                  data={jobState.mapFindings}
                  onFindingClick={onFindingClick}
                  findingsRowStyles={findingsRowStyles}
                />
              )}
            {jobState.esadataFindings !== null &&
              jobState.mapFindingsTypes === 'ASTM Summary' && (
                <div className="table-responsive">
                  <TableAstm bordered hover>
                    <thead>
                      <tr>
                        <TH>Database</TH>
                        <TH>DIST(MI)</TH>
                        <TH>TP</TH>
                        <TH>{'> tp < 1/8'}</TH>
                        <TH>{'1/8 - < 1/4'}</TH>
                        <TH>{'1/4 - < 1/2'}</TH>
                        <TH>1/2 - 1</TH>
                        <TH>{'> 1'}</TH>
                        <TH>Total</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {jobState.esadataFindings !== null &&
                        jobState.esadataFindings.map_findings_summary_astm
                          .length > 0 && (
                          <>
                            {jobState.esadataFindings.map_findings_summary_astm.map(
                              (astm: any, key: any) => (
                                <>
                                  <tr key={`${key}${astm.group}`}>
                                    <TD colSpan={9}>{astm.group}</TD>
                                  </tr>
                                  {astm.summary.length > 0 &&
                                    astm.summary.map(
                                      (summary: any, keys: any) => (
                                        <tr key={`${keys}${summary['prgm']}`}>
                                          <TD>{summary['prgm']}</TD>
                                          <TD>{summary['dist(mi)']}</TD>
                                          <TD>{summary['tp']}</TD>
                                          <TD>{summary['> tp < 1/8']}</TD>
                                          <TD>{summary['1/8 - < 1/4']}</TD>
                                          <TD>{summary['1/4 - < 1/2']}</TD>
                                          <TD>{summary['1/2 - 1']}</TD>
                                          <TD>{summary['> 1']}</TD>
                                          <TD>{summary['Total']}</TD>
                                        </tr>
                                      )
                                    )}
                                </>
                              )
                            )}
                          </>
                        )}
                    </tbody>
                  </TableAstm>
                </div>
              )}
            {jobState.esadataFindings !== null &&
              jobState.mapFindingsTypes === 'VEC Summary' && (
                <Table bordered hover>
                  <thead>
                    <tr>
                      <TH>Database</TH>
                      <TH>TP</TH>
                      <TH>Crossgradient</TH>
                      <TH>Downgradient</TH>
                      <TH>Upgradient</TH>
                      <TH>Total</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {jobState.esadataFindings !== null &&
                      typeof jobState.esadataFindings
                        .map_findings_summary_vec !== 'undefined' &&
                      jobState.esadataFindings.map_findings_summary_vec.length >
                        0 && (
                        <>
                          {jobState.esadataFindings.map_findings_summary_vec.map(
                            (vec: any) => (
                              <>
                                <tr>
                                  <TD colSpan={6}>{vec.group}</TD>
                                </tr>
                                {vec.summary.length > 0 &&
                                  vec.summary.map((summary: any) => (
                                    <tr>
                                      <TD>{summary['prgm']}</TD>
                                      <TD>{summary['tp']}</TD>
                                      <TD>{summary['crossgradient']}</TD>
                                      <TD>{summary['downgradient']}</TD>
                                      <TD>{summary['upgradient']}</TD>
                                      <TD>{summary['Total']}</TD>
                                    </tr>
                                  ))}
                              </>
                            )
                          )}
                        </>
                      )}
                  </tbody>
                </Table>
              )}
            {jobState.esadataFindings !== null &&
              jobState.mapFindingsTypes === 'Oil-Gas Summary' && (
                <TableAstm bordered hover>
                  <thead>
                    <tr>
                      <TH>Database</TH>
                      <TH>TP</TH>
                      <TH>{'1/4 - < 1/2'}</TH>
                      <TH>{'1/8 - < 1/4'}</TH>
                      <TH>{'> 1/2'}</TH>
                      <TH>{'> tp < 1/8'}</TH>
                      <TH>Total</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {jobState.esadataFindings !== null &&
                      typeof jobState.esadataFindings
                        .map_findings_summary_oil_gas !== 'undefined' &&
                      Object.keys(
                        jobState.esadataFindings.map_findings_summary_oil_gas
                      ).length > 0 && (
                        <>
                          <tr>
                            <TD colSpan={7}>
                              {
                                jobState.esadataFindings
                                  .map_findings_summary_oil_gas.group
                              }
                            </TD>
                          </tr>
                          {jobState.esadataFindings.map_findings_summary_oil_gas
                            .summary.length > 0 &&
                            jobState.esadataFindings.map_findings_summary_oil_gas.summary.map(
                              (summary: any) => (
                                <tr>
                                  <TD>{summary['prgm']}</TD>
                                  <TD>{summary['tp']}</TD>
                                  <TD>{summary['1/4 - < 1/2']}</TD>
                                  <TD>{summary['1/8 - < 1/4']}</TD>
                                  <TD>{summary['> 1/2']}</TD>
                                  <TD>{summary['> tp < 1/8']}</TD>
                                  <TD>{summary['Total']}</TD>
                                </tr>
                              )
                            )}
                        </>
                      )}
                  </tbody>
                </TableAstm>
              )}

            {jobState.esadataFindings !== null &&
              jobState.mapFindingsTypes === 'Database Description' && (
                <DatabaseDescription />
              )}

            {jobState.esadataFindings !== null &&
              jobState.mapFindingsTypes === 'Soils Summary' && <SoilSummary />}

            {jobState.mapFindingsTypes === 'Orphans Summary' &&
              jobState.esadataFindings !== null &&
              typeof jobState.esadataFindings.orphans !== 'undefined' &&
              jobState.esadataFindings.orphans !== null &&
              Object.keys(jobState.esadataFindings.orphans).length > 0 && (
                <StyledDataTable
                  columns={orphansCol}
                  data={jobState.esadataFindings.orphans}
                  noHeader={true}
                  fixedHeader={false}
                  pagination={false}
                  pointerOnHover
                  sortServer={true}
                  onRowClicked={onOrphansClick || undefined}
                  highlightOnHover
                  conditionalRowStyles={orphansRowStyles}
                />
              )}
            {isAdjoiningFindingsType(jobState.mapFindingsTypes) &&
              jobState.esaData &&
              Object.keys(jobState.esaData).length > 0 &&
              typeof jobState.esaData.adjoining_properties_boundaries
                .features !== 'undefined' &&
              typeof jobState.esaData.adjoining_properties_boundaries
                .features[0].geometry !== 'undefined' &&
              Object.keys(
                jobState.esaData.adjoining_properties_boundaries.features
              ).length > 0 &&
              jobState.esaData.adjoining_properties_boundaries &&
              Array.isArray(jobState.adjoiningPropertiesBoundaries) &&
              jobState.adjoiningPropertiesBoundaries.length > 0 && (
                <RecordTable
                  columns={adjoiningCol}
                  data={jobState.adjoiningPropertiesBoundaries}
                  findingsRowStyles={addjoinFindingsRowStyles}
                />
              )}
          </JobTasksContainer>
        </FindingsPanelShell>
      )}
      {jobState.jobFeatureTab && hasJobFeatureData(jobState.jobFeature) && (
        <EntityPanelShell lg={3}>
          <PanelHeader>
            <PanelBackButton
              size="sm"
              onClick={() => {
                dispatch(
                  updateJob({
                    ...jobState,
                    ...{
                      jobFeatureTab: false,
                    },
                  })
                );
              }}
            >
              <ChevronLeft />
            </PanelBackButton>
          </PanelHeader>
          <JobDetailsContainer>
            {isLoading || !displayEntity ? (
              <SpinnerContainer>
                <Spinner animation="border" variant="light" />
              </SpinnerContainer>
            ) : (
              <>
                <EnameText>{displayEntity.entity_name}</EnameText>
                <AddressText>{displayEntity.address}</AddressText>
                <h6>
                  <SpaceEvenly>
                    {displayEntity.entity_source === 'TCEQ' && (
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getTCEQRecordUrl(entityNumber)}
                      >
                        {entityNumber}
                      </a>
                    )}
                    {displayEntity.entity_source === 'EPA' && (
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getEPARecordUrl(entityNumber)}
                      >
                        {entityNumber}
                      </a>
                    )}
                    {displayEntity.entity_source === 'TCEQ-EPA' && (
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getTCEQRecordUrl(entityNumber)}
                      >
                        {entityNumber}
                      </a>
                    )}
                    {displayEntity.secondary_id && (
                      <OverlayTrigger
                        trigger="click"
                        rootClose
                        placement="bottom"
                        overlay={
                          <Popover id="popover">
                            <Popover.Title>EPA IDs</Popover.Title>
                            <Popover.Content>
                              {displayEntity.secondary_id
                                .split(',')
                                .map((epaId: any) => {
                                  if (
                                    epaId.startsWith('11') &&
                                    epaId.length === 12
                                  ) {
                                    return (
                                      <p>
                                        <a
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          href={getEPARecordUrl(epaId)}
                                        >
                                          {epaId}
                                        </a>
                                      </p>
                                    );
                                  }
                                  return '';
                                })}
                            </Popover.Content>
                          </Popover>
                        }
                      >
                        <EPALink>EPA IDs</EPALink>
                      </OverlayTrigger>
                    )}
                  </SpaceEvenly>
                </h6>
                <CustomTabs
                  defaultActiveKey="details"
                  id="uncontrolled-tab-example"
                >
                  <Tab eventKey="details" title="Details">
                    <TreeView details={entityDetails} />
                  </Tab>
                  <Tab eventKey="documents" title={documentsTabTitle}>
                    <OLContainer>
                      <Scrollbars
                        style={{
                          height: 'calc(100vh - 305px)',
                        }}
                      >
                        {R.compose(
                          R.map((relatedRecord: ISearchRecord) => {
                            return (
                              <li
                                style={{
                                  color: relatedRecord.containsSearchTerm
                                    ? commonColors.accent
                                    : commonColors.textPrimary,
                                }}
                                key={relatedRecord.contentId}
                              >
                                {authContext.isTier(TIERS.FREE) && !authContext.isLubrizol() ? (
                                  getRecordTitle(relatedRecord)
                                ) : (
                                  <DownloadHyperlink
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        props.doStorageDownload(
                                          relatedRecord.entitySource
                                        )
                                      ) {
                                        const storageRef = props.getStorageRef(
                                          relatedRecord.entitySource
                                        );
                                        if (R.isNil(storageRef)) {
                                          return toast.error(
                                            `Oops, could not find the file.`
                                          );
                                        }
                                        props.setDownloadTriggers(
                                          R.append(
                                            relatedRecord.contentId,
                                            props.downloadTriggers
                                          )
                                        );
                                        return storageRef
                                          .ref(relatedRecord.fileName)
                                          .getDownloadURL()
                                          .then((url: string | undefined) =>
                                            window.open(
                                              url,
                                              '_blank',
                                              'noopener,noreferrer'
                                            )
                                          )
                                          .catch((err: any) =>
                                            toast.error(err.message)
                                          )
                                          .finally(() =>
                                            props.setDownloadTriggers(
                                              R.without(
                                                [relatedRecord.contentId],
                                                props.downloadTriggers
                                              )
                                            )
                                          );
                                      }
                                      if (!relatedRecord.fileUrl) {
                                        return toast.warn(
                                          <span>
                                            No associated file was found for the
                                            content id{' '}
                                            <b>{relatedRecord.contentId}</b>
                                          </span>
                                        );
                                      }
                                      window.open(
                                        relatedRecord.fileUrl,
                                        '_blank'
                                      );
                                      // props.setRelatedDocument(relatedRecord);
                                    }}
                                  >
                                    {getRecordTitle(relatedRecord)}
                                  </DownloadHyperlink>
                                )}
                              </li>
                            );
                          }),
                          R.sort(
                            R.comparator(
                              (
                                itemOne: ISearchRecord,
                                itemTwo: ISearchRecord
                              ) => itemOne.releaseDate > itemTwo.releaseDate
                            )
                          )
                        )(relatedRecords)}
                        {relatedRecords.length < 1
                          ? 'No documents currently available.'
                          : ''}
                      </Scrollbars>
                    </OLContainer>
                  </Tab>
                </CustomTabs>
              </>
            )}
          </JobDetailsContainer>
        </EntityPanelShell>
      )}
    </>
  );
};

export default MyTundraMapsFinding;
